import { SessionTypes, SessionEvents, ParticipantTypes } from "./types"
import { handlePeerNegotiation, sendNegotiationOffer, attachPeerConnectionDiagnosticListeners } from "./shared.js"

export class Participant {
  #eventListeners = new Map()
  #customLocalMediaStream = null

  /**
   * 
   * @param {string} peerId - the id of the peer
   * @param {RTCPeerConnection} rtcPeerConnection - the RTCPeerConnection object
   * @param {ConnectionContext} context - the connection context
   * @param {any} dataChannelMessageListener - a callback for data channel messages. if not set, defaults to peer-to-peer listener
   */
  constructor({ peerId, rtcPeerConnection, context, participantType, dataChannelMessageListener, displayName }) {
    this.peerId = peerId
    this.rtcPeerConnection = rtcPeerConnection
    this.context = context
    this.participantType = participantType
    this.displayName = displayName
    this.mediaStream = null
    this.dataChannel = null
    this.dataChannelMessageListener = dataChannelMessageListener || this.onDataChannelMessage.bind(this)
    this.localIceCandidates = []
    this.makingNegotiationOffer = false
    this.screenShareTrackSender = null  // RTPSender from addTrack; used by WebRTCSession
    this.screenShareMediaStreamId = null

    if (!this.peerId) {
      throw new Error("peerId is required")
    }
    if (!this.rtcPeerConnection) {
      throw new Error("rtcPeerConnection is required")
    }
    if (!this.context) {
      throw new Error("context is required")
    }
    if (!this.participantType) {
      throw new Error("participantType is required")
    }
  }

  // for backwards compatibility with UI
  get participantId() {
    // console.warn(`participantId is deprecated. use peerId instead: ${this.peerId}`);
    return String(this.peerId);
  }

  /**
   * Mutes this participant's incoming audio (outgoing audio is uneffected)
   */
  muteAudio() {
    this.setAudioEnabled(false);
  }

  /**
   * Unmutes this participant's incoming audio (outgoing audio is uneffected)
   */
  unmuteAudio() {
    this.setAudioEnabled(true);
  }

  setAudioEnabled(isEnabled) {
    if (this.mediaStream) {
      this.mediaStream.getAudioTracks().forEach((trk) => {
        trk.enabled = isEnabled;
      });
    }
  }

  get isPatient() {
    return this.participantType === ParticipantTypes.device;
  }

  /**
   * Adds an event callback
   * @param {string} eventType The type of event
   * @param {string} eventCallback A callback
   */
  on(eventType, eventCallback) {
    if (this.#eventListeners.has(eventType)) {
      console.error(`Participant(${this.peerId}): listener already registered for event '${eventType}'. Replacing existing listener.`);
    }
    this.#eventListeners.set(eventType, eventCallback);
  }

  emit(eventType, ...eventArgs) {
    const callback = this.#eventListeners.get(eventType);
    if (callback) {
      // unwrap varargs and call callback
      callback.apply(null, eventArgs);
    }
  }

  addLocalMediaTrack(track, stream) {
    this.rtcPeerConnection.addTrack(track, stream);
    console.debug(`added local track ${track.kind} to peer ${this.peerId}`, track);
  }

  async connectLocalMedia() {
    const localMediaStream = this.#customLocalMediaStream ? this.#customLocalMediaStream : this.context.localMediaStream;
    if (localMediaStream) {
      console.debug(`connecting local media. state: ${this.rtcPeerConnection.connectionState}/ ${this.rtcPeerConnection.signalingState}`);
      localMediaStream.getTracks().forEach((track) => {
        this.addLocalMediaTrack(track, localMediaStream);
      });
    } else if (this.context.sessionType !== SessionTypes.test) {
      console.error("no local media stream available");
      this.emit("error", "no-local-media-stream");
    }
  }

  collectIceCandidates(maxWaitTimeMs = 1500) {
    this.localIceCandidates = [];
    this.rtcPeerConnection.addEventListener('icecandidate', (event) => {
      if (event.candidate) {
        this.localIceCandidates.push(event.candidate);
        console.debug(`added local ice candidate to ${this.peerId} connection`, event.candidate);
      }
    });

    if (this.rtcPeerConnection.iceGatheringState === 'complete') {
      return Promise.resolve(this.localIceCandidates);
    }

    return new Promise((resolve) => {
      const timeout = setTimeout(() => {
        console.debug(`ice candidate collection timed out for ${this.peerId}`);
        resolve(this.localIceCandidates);
      }, maxWaitTimeMs);
      this.rtcPeerConnection.addEventListener('icegatheringstatechange', () => {
        if (this.rtcPeerConnection.iceGatheringState === 'complete') {
          clearTimeout(timeout);
          console.debug(`ice candidate collection complete for ${this.peerId}`);
          resolve(this.localIceCandidates);
        }
      });
    });
  }

  async onDataChannelMessage(payload) {
    switch (payload.type) {
      case "get-display-info": {
        // a browser peer is requesting this user's display name
        const displayName = this.context.options.userDisplayName;
        this.dataChannel.send(JSON.stringify({
          "type": "display-info-answer",
          displayName,
        }));
        console.debug(`sent get-display-info (${displayName}) to ${this.peerId}`);
        break;
      }
      case "user-bye":
        // assuming the other peer disconnection logic can handle this
        console.info(`received user-bye from ${payload.peer_id}`);
        break;
      case "screen-share-start":
        // the event alerts that a screen share is starting and provides the media stream id so 
        // that the receiving peer can identify the screen share track in the incoming media stream
        console.info(`Received screen share start event from ${payload.fromPeerId} with stream id ${payload.screenShareMediaStreamId}`);
        this.screenShareMediaStreamId = payload.screenShareMediaStreamId;
        break;
      case "screen-share-stop":
        console.info(`Received screen share stop event from ${payload.fromPeerId}`);
        this.screenShareMediaStreamId = null;
        this.emit("session-event", SessionEvents.screenShareStopped, this);
        break;
      case "peer-negotiation":
        console.debug(`received peer-negotiation from ${payload.fromPeerId}.`, payload);
        try {
          await handlePeerNegotiation({
            rtcPeerConnection: this.rtcPeerConnection,
            dataChannel: this.dataChannel,
            localPeerId: this.peerId,
            remotePeerId: payload.fromPeerId,
            makingOffer: this.makingNegotiationOffer,
            sdp: payload.sdp,
          });
        } catch (err) {
          console.error(`error handling peer negotiation offer from ${payload.fromPeerId}`, err);
        }
        break;
      default:
        console.error(`unexpected data channel payload type: ${payload.type}`);
    }
  }

  configurePeerConnectionListeners() {
    const peerConn = this.rtcPeerConnection;
    const self = this;
    peerConn.addEventListener('track', (event) => {
      console.debug(`remote track event for ${this.peerId} ${typeof (this)}`, event, this);
      // https://developer.mozilla.org/en-US/docs/Web/API/RTCPeerConnection/track_event
      if (this.screenShareMediaStreamId && event.streams[0].id === this.screenShareMediaStreamId) {
        console.debug(`received screen share track for ${this.peerId} with stream id ${this.screenShareMediaStreamId}`);
        this.emit("session-event", SessionEvents.participantScreenShareTrackAdded, self, event.streams[0]);
        return;
      }
      this.mediaStream = event.streams[0];
      this.emit("session-event", SessionEvents.participantTrackAdded, self);
    })

    attachPeerConnectionDiagnosticListeners(peerConn, this.peerId);
    peerConn.addEventListener('negotiationneeded', async () => {
      console.debug(`negotiation needed for ${this.peerId}`);
      this.makingNegotiationOffer = true;
      try {
        await sendNegotiationOffer({
          rtcPeerConnection: this.rtcPeerConnection,
          dataChannel: this.dataChannel,
          localPeerId: this.peerId,
        });
      } finally {
        this.makingNegotiationOffer = false;
      }
    });

    peerConn.addEventListener('connectionstatechange', () => {
      console.debug(`${this.peerId} connection state changed to ${peerConn.connectionState}`);
      if (peerConn.connectionState === 'disconnected' || peerConn.connectionState === 'failed') {
        this.emit("disconnect", this);
      }
    })

    // for data channel to the video device, the channel is used to coordinate with other peers in a meeting.
    // data channels for peer-to-peer connections may also be setup for direct communication.
    // for example, the kiosk viewer uses a data channel to ask for the display name of the user
    peerConn.addEventListener("datachannel", (event) => {
      console.debug("data channel added event", event, this);
      this.dataChannel = event.channel;
      // add channel event listeners
      this.dataChannel.addEventListener("message", async (event) => {
        console.debug("data channel message", event, this);
        const payload = JSON.parse(event.data);
        await this.dataChannelMessageListener(payload);
      })
    })
  }

  async configureRemote(remoteSdp, remoteIceCandidates, customLocalMediaStream = null) {
    this.#customLocalMediaStream = customLocalMediaStream;
    const peerConn = this.rtcPeerConnection;
    // let expectedRemoteTracks = 0;

    if (!remoteSdp) {
      throw new Error("no remote SDP provided");
    }
    console.debug(`configuring remote sdp for ${this.peerId}. state: ${peerConn.connectionState}/ ${peerConn.signalingState}`);
    this.configurePeerConnectionListeners();

    await peerConn.setRemoteDescription(remoteSdp);

    if (remoteIceCandidates) {
      remoteIceCandidates.forEach((cand) => {
        this.rtcPeerConnection.addIceCandidate(cand);
        console.debug(`added ice candidate to device ${this.peerId} connection`, cand);
      });
    }

    console.debug("connecting local media...")
    await this.connectLocalMedia();
  }

  async initializePeerConnection() {
    console.debug(`creating offer for ${this.peerId}`);
    // NOTE: assume caller is in charge of ice collection
    this.configurePeerConnectionListeners();
    this.dataChannel = this.rtcPeerConnection.createDataChannel("browser-peer")
    this.dataChannel.addEventListener("message", async (event) => {
      console.debug("data channel message", event, this);
      const payload = JSON.parse(event.data);
      await this.dataChannelMessageListener(payload);
    })
    await this.connectLocalMedia();

    // Ensure the offer includes a video m= line even when not sending video,
    // so the answerer can send their video in the initial answer.
    // Without this, the answerer's addTrack(videoTrack) creates an unmatched
    // transceiver that can't be included in the answer, and the subsequent
    // negotiationneeded fires before the data channel is open, leaving the
    // negotiation-needed flag stuck and blocking future renegotiations
    // (e.g. screen share).
    const hasVideoSender = this.rtcPeerConnection.getSenders().some(s => s.track?.kind === 'video');
    if (!hasVideoSender) {
      this.rtcPeerConnection.addTransceiver('video', { direction: 'recvonly' });
    }
  }

  /**
   * Disconnects the participant's resources
   */
  disconnect() {
    console.debug(`disconnecting participant ${this.peerId}`);
    if (this.dataChannel && this.dataChannel.readyState === 'open') {
      try {
        this.dataChannel.send(JSON.stringify({
          peer_id: this.peerId,  // send the peer id of the participant that is disconnecting
          type: 'user-bye'
        }));
        console.debug(`sent user-bye to ${this.peerId}`);
      } catch (err) {
        console.warn(`error sending user-bye to ${this.peerId}`, err);
      }

      // close after a slight delay to make sure message goes through  
      setTimeout(() => {
        this.dataChannel.close();
        console.debug("data channel closed");
      }, 100);
    } else {
      console.debug(`data channel not open for ${this.peerId}`);
    }
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach((trk) => {
        trk.stop();
        console.debug(`stopped remote ${trk.kind} track (${trk.id}) for ${this.peerId}`, trk);
      });
      // "kiosk-viewer" for basic_mode; device-kiosk- for pusher
    } else if (this.peerId !== 'kiosk-viewer' && !this.peerId.startsWith('device-kiosk')) {  // kiosk-viewer is a special participant that doesn't send a media stream
      console.warn(`participant ${this.peerId} had no media stream while disconnecting`);
    }
    if (this.#customLocalMediaStream) {
      console.log(`disconnecting custom local stream for ${this.peerId}`, this.#customLocalMediaStream);
      this.#customLocalMediaStream.getTracks().forEach((track) => {
        track.stop();
        console.debug(`stopped custom local stream ${track.kind} track (${track.id})`, track);
      });
      this.#customLocalMediaStream = null;
    } else {
      console.debug(`participant ${this.peerId} had no local stream while disconnecting`);
    }
    if (this.rtcPeerConnection) {
      this.rtcPeerConnection.close();
      console.debug(`closed peer connection to ${this.peerId}`);
    } else {
      console.warn(`participant ${this.peerId} had no peer connection while disconnecting`);
    }
    this.mediaStream = null;
    this.rtcPeerConnection = null;
    this.#eventListeners.clear();
    this.localIceCandidates = [];
  }
}